import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import ImageUploadWithCrop from '../Common/ImageUploadWithCrop';
import { passkitAPI } from '../../services/api';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { logger } from '../../utils/logger';

interface TicketTypeTemplate {
  id: string;
  name: string;
  description?: string;
  passkit_template_id: string;
}

interface Venue {
  id: string;
  name: string;
  address?: string;
}

interface TicketTypeCreationDialogProps {
  open: boolean;
  onClose: () => void;
  productionId: string;
  productionName: string;
  onSuccess: () => void;
}

const TicketTypeCreationDialog: React.FC<TicketTypeCreationDialogProps> = ({
  open,
  onClose,
  productionId,
  productionName,
  onSuccess
}) => {
  // Step state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Osnovni Podaci', 'Slike', 'Mesto i Datumi'];

  // Template selection
  const [templates, setTemplates] = useState<TicketTypeTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Venues
  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [createNewVenue, setCreateNewVenue] = useState(false);
  const [newVenueName, setNewVenueName] = useState('');
  const [newVenueAddress, setNewVenueAddress] = useState('');
  const [newVenueTimezone, setNewVenueTimezone] = useState('Europe/Belgrade');
  const [loadingVenues, setLoadingVenues] = useState(false);

  // Form data
  const [ticketTypeName, setTicketTypeName] = useState('');

  // Terms & Conditions
  const [useBaseTerms, setUseBaseTerms] = useState(true);
  const [customTerms, setCustomTerms] = useState('');

  // Images - 2 required images (logo i icon se kopiraju sa base template-a)
  const [thumbnailImageFile, setThumbnailImageFile] = useState<File | null>(null);
  const [thumbnailImageUrl, setThumbnailImageUrl] = useState<string | null>(null);
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);

  // Event dates
  const [eventDates, setEventDates] = useState<Dayjs[]>([dayjs()]);

  // Create state
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Load data when dialog opens
  useEffect(() => {
    if (open && productionId) {
      loadTemplates();
      loadVenues();
    } else {
      resetDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productionId]);

  const resetDialog = () => {
    setActiveStep(0);
    setTemplates([]);
    setSelectedTemplateId('');
    setVenues([]);
    setSelectedVenueId('');
    setCreateNewVenue(false);
    setNewVenueName('');
    setNewVenueAddress('');
    setNewVenueTimezone('Europe/Belgrade');
    setTicketTypeName('');
    setUseBaseTerms(true);
    setCustomTerms('');
    setThumbnailImageFile(null);
    setThumbnailImageUrl(null);
    setHeroImageFile(null);
    setHeroImageUrl(null);
    setEventDates([dayjs()]);
    setError('');
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      setError('');

      const response = await passkitAPI.getTicketTypeTemplates(productionId);

      if (response.templates && response.templates.length > 0) {
        setTemplates(response.templates);
        // Auto-select first template
        setSelectedTemplateId(response.templates[0].id);
      } else {
        setError('Nema dostupnih templatea za ovu produkciju');
      }
    } catch (err: any) {
      logger.error('Error loading templates:', err);
      setError('Greška pri učitavanju templatea');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadVenues = async () => {
    try {
      setLoadingVenues(true);
      const response = await passkitAPI.listVenues();

      if (response.venues) {
        setVenues(response.venues);
      }
    } catch (err: any) {
      logger.error('Error loading venues:', err);
      // Don't show error - venues are optional
    } finally {
      setLoadingVenues(false);
    }
  };

  const handleAddEventDate = () => {
    setEventDates([...eventDates, dayjs()]);
  };

  const handleRemoveEventDate = (index: number) => {
    if (eventDates.length > 1) {
      setEventDates(eventDates.filter((_, i) => i !== index));
    }
  };

  const handleEventDateChange = (index: number, date: Dayjs | null) => {
    if (date) {
      const newDates = [...eventDates];
      newDates[index] = date;
      setEventDates(newDates);
    }
  };

  const canProceedToNextStep = (): boolean => {
    switch (activeStep) {
      case 0: // Basic info
        return ticketTypeName.trim() !== '' && selectedTemplateId !== '';
      case 1: // Images (logo i icon se kopiraju sa base template-a)
        return thumbnailImageFile !== null &&
               heroImageFile !== null;
      case 2: // Venue and dates
        if (createNewVenue) {
          return newVenueName.trim() !== '' && newVenueAddress.trim() !== '';
        }
        return selectedVenueId !== '';
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (canProceedToNextStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError('');

      // Convert images to base64
      const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result as string;
            // Remove data:image/...;base64, prefix
            const base64Data = base64.split(',')[1];
            resolve(base64Data);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      const thumbnailBase64 = thumbnailImageFile ? await convertToBase64(thumbnailImageFile) : '';
      const heroBase64 = heroImageFile ? await convertToBase64(heroImageFile) : '';

      // Build request payload (logo i icon se kopiraju automatski sa base template-a na backendu)
      const payload: any = {
        ticket_type_name: ticketTypeName,
        base_template_id: selectedTemplateId,
        images: {
          thumbnail: thumbnailBase64,
          hero: heroBase64
        },
        event_dates: eventDates.map(date => date.toISOString()),
        use_base_terms_and_conditions: useBaseTerms
      };

      // Dodaj custom Terms & Conditions ako nisu base
      if (!useBaseTerms && customTerms.trim()) {
        payload.ticket_type_conditions = customTerms;
      }

      // Add venue info
      if (createNewVenue) {
        payload.venue = {
          name: newVenueName,
          address: newVenueAddress,
          timezone: newVenueTimezone
        };
      } else {
        payload.venue_id = selectedVenueId;
      }

      // Create ticket type with events
      await passkitAPI.createTicketTypeWithEvents(productionId, payload);

      onSuccess();
      onClose();
    } catch (err: any) {
      logger.error('Error creating ticket type:', err);
      setError(err.response?.data?.detail || 'Greška pri kreiranju ticket type-a');
    } finally {
      setCreating(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Kreirajte novi tip karte sa custom dizajnom
            </Typography>

            <TextField
              fullWidth
              label="Naziv Ticket Type-a"
              value={ticketTypeName}
              onChange={(e) => setTicketTypeName(e.target.value)}
              placeholder="npr. VIP Karta, Regular, Student"
              sx={{ mb: 3 }}
              required
            />

            <FormControl fullWidth required sx={{ mb: 3 }}>
              <InputLabel>Base Template</InputLabel>
              <Select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                label="Base Template"
                disabled={loadingTemplates || templates.length === 0}
              >
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Terms & Conditions */}
            <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Terms & Conditions (Uslovi korišćenja)
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={useBaseTerms}
                    onChange={(e) => setUseBaseTerms(e.target.checked)}
                  />
                }
                label="Preuzmi sa base template-a"
                sx={{ mb: useBaseTerms ? 0 : 2 }}
              />

              {!useBaseTerms && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Custom Terms & Conditions"
                  value={customTerms}
                  onChange={(e) => setCustomTerms(e.target.value)}
                  placeholder="Unesite custom Terms & Conditions za ovaj ticket type..."
                  helperText="Ovi uslovi će se prikazati korisnicima na pass-u"
                />
              )}
            </Paper>

            {loadingTemplates && (
              <Box display="flex" justifyContent="center" mt={2}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Dodajte slike za ticket type. Logo i Icon se automatski kopiraju sa base template-a.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Dimenzije slika:</strong>
              </Typography>
              <Typography variant="caption" component="div">
                • Thumbnail: 270x270px minimum, aspect ratio 2:3 do 3:2 (prikazuje se na pass-u, zadržava background)<br />
                • Hero: 1032x336px (Google Pay header)<br />
                • Logo i Icon se automatski kopiraju sa base template-a
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Thumbnail Image */}
              <ImageUploadWithCrop
                value={thumbnailImageUrl}
                onChange={(file, url) => {
                  setThumbnailImageFile(file);
                  setThumbnailImageUrl(url);
                }}
                label="Thumbnail (270x270+ min)"
                helperText="Slika za prikaz na pass-u (zadržava background)"
                aspectRatio={1}
                outputSize={{ width: 540, height: 540 }}
              />

              {/* Hero Image */}
              <ImageUploadWithCrop
                value={heroImageUrl}
                onChange={(file, url) => {
                  setHeroImageFile(file);
                  setHeroImageUrl(url);
                }}
                label="Hero (1032x336)"
                helperText="Header za Google Pay"
                aspectRatio={1032 / 336}
                outputSize={{ width: 1032, height: 336 }}
              />
            </Box>
          </Box>
        );

      case 2:
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Izaberite mesto i datume događaja
              </Typography>

              {/* Venue Selection */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Mesto održavanja
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={createNewVenue}
                      onChange={(e) => setCreateNewVenue(e.target.checked)}
                    />
                  }
                  label="Kreiraj novo mesto"
                  sx={{ mb: 2 }}
                />

                {createNewVenue ? (
                  <Box>
                    <TextField
                      fullWidth
                      label="Naziv mesta"
                      value={newVenueName}
                      onChange={(e) => setNewVenueName(e.target.value)}
                      sx={{ mb: 2 }}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Adresa"
                      value={newVenueAddress}
                      onChange={(e) => setNewVenueAddress(e.target.value)}
                      sx={{ mb: 2 }}
                      required
                    />
                    <TextField
                      fullWidth
                      label="Vremenska zona"
                      value={newVenueTimezone}
                      onChange={(e) => setNewVenueTimezone(e.target.value)}
                      placeholder="Europe/Belgrade"
                    />
                  </Box>
                ) : (
                  <FormControl fullWidth required>
                    <InputLabel>Izaberi postojeće mesto</InputLabel>
                    <Select
                      value={selectedVenueId}
                      onChange={(e) => setSelectedVenueId(e.target.value)}
                      label="Izaberi postojeće mesto"
                      disabled={loadingVenues}
                    >
                      {venues.map((venue) => (
                        <MenuItem key={venue.id} value={venue.id}>
                          {venue.name} {venue.address && `- ${venue.address}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Paper>

              {/* Event Dates */}
              <Paper sx={{ p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle1">
                    Datumi događaja
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddEventDate}
                  >
                    Dodaj datum
                  </Button>
                </Box>

                {eventDates.map((date, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={1} mb={2}>
                    <DateTimePicker
                      label={`Događaj ${index + 1}`}
                      value={date}
                      onChange={(newDate) => {
                        // Set seconds to 00 when changing date
                        const dateWithoutSeconds = newDate ? (newDate as Dayjs).second(0).millisecond(0) : null;
                        handleEventDateChange(index, dateWithoutSeconds);
                      }}
                      format="DD.MM.YYYY HH:mm"
                      views={['year', 'month', 'day', 'hours', 'minutes']}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                    {eventDates.length > 1 && (
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveEventDate(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Paper>
            </Box>
          </LocalizationProvider>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Kreiraj Ticket Type
        <Typography variant="caption" display="block" color="text.secondary">
          Produkcija: {productionName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>
          Otkaži
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={creating}>
            Nazad
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceedToNextStep()}
          >
            Sledeće
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !canProceedToNextStep()}
          >
            {creating ? <CircularProgress size={24} /> : 'Kreiraj'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TicketTypeCreationDialog;
